 
import axios, { AxiosResponse, CancelTokenSource } from 'axios'
import { put, takeEvery, select, call } from 'redux-saga/effects'
import { IClient } from 'shared/models/Client'
import { ICommonListModel } from 'shared/models/Common'
import { getActiveClient } from 'shared/store/clients/selectors'
import {
  deleteEmailList,
  deleteMessage,
  getEmail,
  getEmails,
  getEmailStatistics,
  getEmailSeenBy,
  getEmailSeenBySummary,
  getJunkEmail,
  getJunkEmails,
  handleJunkEmail,
  patchEmail,
  setEmailToRead,
} from 'Modules/Communication/Email/shared/api/emails.api'
import {
  EmailTypeEnum,
  IEmailListItem,
  IEmailModel,
  IEmailStatistics,
  IEmailSeenBy,
  IEmailSeenByUserSummary,
  IMoveJunkModel,
  JunkDestinationEnum,
} from 'Modules/Communication/Email/shared/models/Email'
import {
  getCurrentEmail,
  getEmails as getEmailsSelectors,
  getEmailsMarkedForActions,
  getEmailsSection,
} from './selectors'

import {
  EMAILS_FETCH_MORE_REQUESTED,
  EMAILS_FETCH_MORE_REQUEST_ERROR,
  EMAILS_FETCH_MORE_REQUEST_SUCCESS,
  EMAILS_FETCH_REQUESTED,
  EMAILS_FETCH_REQUEST_ERROR,
  EMAILS_FETCH_REQUEST_SUCCESS,
  EMAILS_MARKED_UPDATE_READ_STATUS_REQUESTED,
  EMAILS_MARKED_UPDATE_READ_STATUS_SINGLE_REQUEST,
  EMAILS_MARKED_UPDATE_READ_STATUS_SUCCESS,
  EMAILS_SET_FILTERS,
  EMAIL_FETCH_REQUEST_ERROR,
  EMAIL_FETCH_REQUEST_SUCCESS,
  EMAIL_RESET_CURRENT,
  EMAIL_SET_CURRENT,
  EMAILS_FETCH_NEW_SILENTLY_REQUESTED,
  EMAILS_FETCH_NEW_SILENTLY_SUCCESS,
  EMAILS_SET_HANDLED_BY_SUCCESS,
  EMAILS_SET_HANDLED_BY_REQUESTED,
  EMAILS_REMOVE_HANDLED_BY_REQUESTED,
  EMAILS_REMOVE_HANDLED_BY_SUCCESS,
  EMAIL_DELETE_REQUESTED,
  EMAIL_DELETE_SUCCESS,
  EMAIL_DELETE_ERROR,
  EMAIL_RESTORE_REQUESTED,
  EMAIL_RESTORE_SUCCESS,
  EMAIL_FETCH_SILENT_REQUESTED,
  EMAIL_FETCH_SILENT_SUCCESS,
  EMAIL_FETCH_SILENT_ERROR,
  EMAILS_FETCH_FOR_WIDGET,
  EMAILS_FETCH_STATISTICS_REQUESTED,
  EMAILS_FETCH_STATISTICS_SUCCESS,
  EMAIL_FETCH_SEEN_BY_REQUESTED,
  EMAIL_FETCH_SEEN_BY_SUCCESS,
  EMAIL_FETCH_SEEN_BY_ERROR,
  EMAIL_FETCH_SEEN_BY_SUMMARY_REQUESTED,
  EMAIL_FETCH_SEEN_BY_SUMMARY_SUCCESS,
  EMAIL_DELETESTATUS_RESET,
  EMAILS_HANDLE_JUNK_REQUESTED,
  EMAILS_HANDLE_JUNK_SUCCESS,
  EMAILS_HANDLE_JUNK_ERROR,
} from './actionTypes'
import { getEmailsFilters } from './selectors'
import { EmailActionTypes } from './types'
import history from 'browserHistory'
import {
  IInboxFilters,
  IInboxPatchModel,
  IInboxPatchSingle,
  InboxSectionEnum,
} from 'Modules/Communication/shared/models/Inbox'
import { closeModal } from 'shared/store/modal'
import { addToasterMessage } from 'shared/store/toaster'
import { convertEnumToReadable } from 'Modules/Communication/shared/util/sectionHelper'

function* updateFilters() {
  yield put({ type: EMAILS_FETCH_REQUESTED })
}

function* fetchWidgetEmails() {
  try {
    const activeClient: IClient = yield select(getActiveClient)

    const filters: IInboxFilters = {
      skip: 0,
      take: 5,
      queryStatus: InboxSectionEnum.Received,
      labelId: '',
      dates: {
        deleted: {
          from: undefined,
          to: undefined,
        },
        processed: {
          from: undefined,
          to: undefined,
        },
        received: {
          from: undefined,
          to: undefined,
        },
        sent: {
          from: undefined,
          to: undefined,
        },
      },
    }

    const response: AxiosResponse<{
      result: ICommonListModel<IEmailListItem>
    }> = yield call(getEmails, {
      ...filters,
      clientId: activeClient.id,
    })

    yield put({
      type: EMAILS_FETCH_REQUEST_SUCCESS,
      payload: response.data,
    })
  } catch (e) {
    if (!axios.isCancel(e))
      yield put({
        type: EMAILS_FETCH_REQUEST_ERROR,
        payload: e,
      })
  }
}

function* fetchEmails(action: EmailActionTypes) {
  try {
    const activeClient: IClient = yield select(getActiveClient)
    const filters: IInboxFilters = yield select(getEmailsFilters)

    const source: CancelTokenSource = action.payload

    let response: ICommonListModel<IEmailListItem>

    if (filters.queryStatus !== InboxSectionEnum.Junk) {
      const emails: AxiosResponse<ICommonListModel<IEmailListItem>> =
        yield call(getEmails, {
          ...filters,
          sortcolumn:
            filters.queryStatus === InboxSectionEnum.Sent
              ? 'sentDate'
              : filters.sortcolumn,
          clientId: activeClient.id,
          source,
        })

      response = emails.data
    } else {
      const junk: AxiosResponse<IEmailListItem[]> = yield call(getJunkEmails, {
        ...filters,
        clientId: activeClient.id,
        source,
      })

      const junkFormatted: ICommonListModel<IEmailListItem> = {
        currentPage: 1,
        morePages: false,
        result: junk.data,
        resultCount: junk.data.length,
        totalCount: junk.data.length,
        totalPageCount: 1,
      }

      response = junkFormatted
    }

    yield put({
      type: EMAILS_FETCH_REQUEST_SUCCESS,
      payload: response,
    })
  } catch (e) {
    if (!axios.isCancel(e))
      yield put({
        type: EMAILS_FETCH_REQUEST_ERROR,
        payload: e,
      })
  }
}

function* fetchMoreEmails() {
  const activeClient: IClient = yield select(getActiveClient)
  const filters: IInboxFilters = yield select(getEmailsFilters)

  try {
    const response: AxiosResponse<{
      result: ICommonListModel<IEmailListItem>
    }> = yield call(getEmails, {
      ...filters,
      sortcolumn:
        filters.queryStatus === InboxSectionEnum.Sent
          ? 'sentDate'
          : filters.sortcolumn,
      clientId: activeClient.id,
    })

    yield put({
      type: EMAILS_FETCH_MORE_REQUEST_SUCCESS,
      payload: response.data,
    })
  } catch (e) {
    yield put({
      type: EMAILS_FETCH_MORE_REQUEST_ERROR,
      payload: e,
    })
  }
}

function* fetchEmail(action: EmailActionTypes) {
  const section: InboxSectionEnum = yield select(getEmailsSection)
  const activeClient: IClient = yield select(getActiveClient)
  const payload = action.payload

  try {
    if (payload.id === null) {
      yield put({
        type: EMAIL_RESET_CURRENT,
      })

      return
    }

    const isJunkmail = section === InboxSectionEnum.Junk

    const message: AxiosResponse<IEmailModel> = isJunkmail
      ? yield call(getJunkEmail, activeClient.id, payload.id)
      : yield call(getEmail, payload.id, payload.tokenSource)

    yield put({
      type: EMAIL_FETCH_REQUEST_SUCCESS,
      payload: message.data,
    })

    if (
      !message.data.readDate &&
      message.data.status === EmailTypeEnum.Received
    ) {
      yield put({
        type: EMAILS_MARKED_UPDATE_READ_STATUS_SINGLE_REQUEST,
        payload: { id: payload.id, isRead: true },
      })
    }

    // Junkmail does not have "seen by summary" & "seen by"
    if (!isJunkmail) {
      try {
        yield call(setEmailToRead, payload.id)
      } catch {
        console.log('Klarte ikke markere e-post som lest')
      }

      yield put({
        type: EMAIL_FETCH_SEEN_BY_SUMMARY_REQUESTED,
        payload: payload.id,
      })
    }
  } catch (e) {
    if (!axios.isCancel(e))
      yield put({
        type: EMAIL_FETCH_REQUEST_ERROR,
        payload: e,
      })
  }
}

function* fetchEmailSilent(action: EmailActionTypes) {
  try {
    const activeId: string = yield select(getCurrentEmail)

    if (!activeId || (action.payload?.id && action.payload.id !== activeId))
      return

    const { data }: AxiosResponse<IEmailModel> = yield call(getEmail, activeId)

    yield put({
      type: EMAIL_FETCH_SILENT_SUCCESS,
      payload: {
        email: data,
        onlyFetchLabels: action.payload?.onlyFetchLabels,
      },
    })
  } catch (e) {
    yield put({
      type: EMAIL_FETCH_SILENT_ERROR,
      payload: e,
    })
  }
}

function* setReadStatusToUnread(action: EmailActionTypes) {
  const { id, isRead } = action.payload as { id: string; isRead: boolean }

  try {
    const payload: IInboxPatchModel = {
      model: [
        {
          id,
          patchModel: [{ op: 'replace', path: '/isRead', value: isRead }],
        },
      ],
    }

    yield call(patchEmail, payload)

    yield put({
      type: EMAILS_MARKED_UPDATE_READ_STATUS_SUCCESS,
      payload: payload.model,
    })
  } catch (e) {
    yield put(
      addToasterMessage({
        text: 'Det skjedde en feil under oppdatering av lest status',
        type: 'danger',
      })
    )
  }
}

function* updateEmailReadStatus() {
  try {
    const { data } = yield select(getEmailsSelectors)
    const markedForActions: string[] = yield select(getEmailsMarkedForActions)
    const list = data as ICommonListModel<IEmailListItem>

    const listItemsThatIsMarked = list.result.filter((item: IEmailListItem) =>
      markedForActions.includes(item.id)
    )

    const payload: IInboxPatchModel = {
      model: [],
    }

    // Clean out every with isRead
    const itemsIsUnread = listItemsThatIsMarked.filter((item) => !item.isRead)

    if (itemsIsUnread.length) {
      const model: IInboxPatchSingle[] = itemsIsUnread.map((item) => ({
        id: item.id,
        patchModel: [{ op: 'replace', path: '/isRead', value: true }],
      }))

      payload.model = model
    } else {
      const model: IInboxPatchSingle[] = listItemsThatIsMarked.map((item) => ({
        id: item.id,
        patchModel: [{ op: 'replace', path: '/isRead', value: false }],
      }))

      payload.model = model
    }

    yield call(patchEmail, payload)

    yield put({
      type: EMAILS_MARKED_UPDATE_READ_STATUS_SUCCESS,
      payload: payload.model,
    })
  } catch (e) {
    yield put(
      addToasterMessage({
        text: 'Det skjedde en feil under oppdatering av lest status',
        type: 'danger',
      })
    )
  }
}

function* fetchNewEmailsSilently(action: EmailActionTypes) {
  const { clientId, section } = action.payload as {
    isOnEmailPage: boolean
    section?: InboxSectionEnum
    clientId?: string
  }

  // Disabled for now
  // if (!isOnEmailPage) return

  try {
    const activeClient: IClient = yield select(getActiveClient)
    const filters: IInboxFilters = yield select(getEmailsFilters)

    if (!clientId || !activeClient || activeClient?.id !== clientId) return
    if (section && filters?.queryStatus !== section) return

    yield put({ type: EMAILS_FETCH_STATISTICS_REQUESTED })

    const response: AxiosResponse<{
      result: ICommonListModel<IEmailListItem>
    }> = yield call(getEmails, {
      ...filters,
      clientId: activeClient.id,
      // Only fetch 5 newest, to avoid fetching too much in the background
      skip: 0,
      take: 5,
    })

    yield put({
      type: EMAILS_FETCH_NEW_SILENTLY_SUCCESS,
      payload: response.data.result || [],
    })
  } catch (e) {
    console.error('Noe skjedde under henting av nye eposter', e)
  }
}

function* addHandledByToEmail(action: EmailActionTypes) {
  try {
    const payload = action.payload as string[]

    const patchModel: IInboxPatchModel = {
      model: payload.map((item) => ({
        id: item,
        patchModel: [{ op: 'replace', path: '/IsProcessed', value: true }],
      })),
    }

    yield call(patchEmail, patchModel)

    yield put({
      type: EMAILS_SET_HANDLED_BY_SUCCESS,
      payload: { messageIds: payload },
    })

    if (payload.length === 1)
      yield put({
        type: EMAIL_RESET_CURRENT,
        payload: { navigateFromCurrent: true },
      })

    yield put({ type: EMAILS_FETCH_STATISTICS_REQUESTED })
    yield put(
      addToasterMessage({
        text: `E-posten${
          payload.length > 1 ? 'e' : ''
        } ble flyttet til mappen "Behandlet"`,
        type: 'success',
      })
    )
  } catch (e) {
    yield put(
      addToasterMessage({
        text: 'Det skjedde en feil ved markering av behandlet',
        type: 'danger',
      })
    )
  }
}

function* removeHandledByToEmail(action: EmailActionTypes) {
  try {
    const patchModel: IInboxPatchModel = {
      model: [
        {
          id: action.payload,
          patchModel: [{ op: 'replace', path: '/IsProcessed', value: false }],
        },
      ],
    }

    yield call(patchEmail, patchModel)
    yield put({
      type: EMAILS_REMOVE_HANDLED_BY_SUCCESS,
      payload: action.payload,
    })

    yield put({
      type: EMAIL_RESET_CURRENT,
      payload: { navigateFromCurrent: true },
    })

    yield put(
      addToasterMessage({
        text: 'E-posten har blitt flyttet tilbake til sin opprinnelige mappe',
        type: 'success',
      })
    )
  } catch (e) {
    yield put(
      addToasterMessage({
        text: 'Det skjedde en feil ved fjerning av behandlet av',
        type: 'danger',
      })
    )
  }
}

function* deleteEmails(action: EmailActionTypes) {
  try {
    const currentId: string = yield select(getCurrentEmail)
    const ids: string[] = action.payload.ids

    const patchList: IInboxPatchSingle[] = []

    if (action.payload.softDelete) {
      ids.map((id) =>
        patchList.push({
          id,
          patchModel: [{ op: 'replace', path: '/isDeleted', value: true }],
        })
      )

      const payload: IInboxPatchModel = {
        model: patchList,
      }

      yield call(patchEmail, payload)
    } else {
      if (ids.length > 1) yield call(deleteEmailList, ids)
      else yield call(deleteMessage, ids[0])
    }

    yield put({
      type: EMAIL_DELETE_SUCCESS,
      payload: ids,
    })

    if (ids.length === 1 && currentId === ids[0])
      yield put({
        type: EMAIL_RESET_CURRENT,
        payload: { navigateFromCurrent: true },
      })

    yield put({ type: EMAILS_FETCH_STATISTICS_REQUESTED })
    yield put({ type: EMAIL_DELETESTATUS_RESET })
    yield put(closeModal())
  } catch (e) {
    yield put({
      type: EMAIL_DELETE_ERROR,
    })
  }
}

function* restoreEmails(action: EmailActionTypes) {
  try {
    const ids: string[] = action.payload
    const section: InboxSectionEnum = yield select(getEmailsSection)

    const patchList: IInboxPatchSingle[] = []

    ids.map((id) =>
      patchList.push({
        id,
        patchModel: [{ op: 'replace', path: '/isDeleted', value: false }],
      })
    )

    const payload: IInboxPatchModel = {
      model: patchList,
    }

    yield call(patchEmail, payload)

    // remove active email
    if (ids.length === 1 && section !== InboxSectionEnum.All) {
      history.push('/kommunikasjon/epost/slettet')
    } else if (section === InboxSectionEnum.All) {
      history.push('/kommunikasjon/epost/etikett')
    }

    yield put({
      type: EMAIL_RESTORE_SUCCESS,
      payload: ids,
    })

    yield put(
      addToasterMessage({
        text: `E-posten${ids.length > 1 ? 'e' : ''} ble gjenopprettet`,
        type: 'success',
      })
    )

    yield put({ type: EMAIL_DELETESTATUS_RESET })
  } catch (e) {
    yield put(
      addToasterMessage({
        text: 'Det skjedde en feil under gjenoppretting av e-post',
        type: 'danger',
      })
    )
  }
}

function* fetchStatistics() {
  try {
    const { id }: IClient = yield select(getActiveClient)

    const response: AxiosResponse<IEmailStatistics> = yield call(
      getEmailStatistics,
      id
    )

    yield put({
      type: EMAILS_FETCH_STATISTICS_SUCCESS,
      payload: response.data,
    })
  } catch (e) {
    console.log(e)
  }
}

function* fetchEmailSeenBy(action: EmailActionTypes) {
  const messageId = action.payload
  try {
    const response: AxiosResponse<IEmailSeenBy> = yield call(
      getEmailSeenBy,
      messageId
    )

    yield put({
      type: EMAIL_FETCH_SEEN_BY_SUCCESS,
      payload: response.data,
    })
  } catch (e) {
    yield put({
      type: EMAIL_FETCH_SEEN_BY_ERROR,
      payload: e,
    })
  }
}

function* fetchEmailSeenBySummary(action: EmailActionTypes) {
  const messageId = action.payload
  try {
    const response: AxiosResponse<IEmailSeenByUserSummary> = yield call(
      getEmailSeenBySummary,
      messageId
    )

    yield put({
      type: EMAIL_FETCH_SEEN_BY_SUMMARY_SUCCESS,
      payload: response.data,
    })
  } catch (e) {
    console.log(e)
  }
}

function* handleMoveJunkEmails(action: EmailActionTypes) {
  const client: IClient = yield select(getActiveClient)
  const payload = action.payload as IMoveJunkModel
  try {
    yield call(handleJunkEmail, client.id, payload)

    yield put({ type: EMAILS_HANDLE_JUNK_SUCCESS })
    yield put(closeModal())

    if (payload.destination === JunkDestinationEnum.Inbox) {
      yield put(
        addToasterMessage({
          text: `Flyttet e-post vil dukke opp i innboks innen kort tid.`,
        })
      )
    }

    // Refetch junk emails
    yield put({ type: EMAILS_FETCH_REQUESTED })
  } catch (e) {
    console.log(e)
    yield put({
      type: EMAILS_HANDLE_JUNK_ERROR,
      payload: e,
    })
  }
}

function* navigateFromCurrent(action: EmailActionTypes) {
  if (!action.payload?.navigateFromCurrent) return
  try {
    const filters: IInboxFilters = yield select(getEmailsFilters)
    const section = convertEnumToReadable(
      filters?.queryStatus || InboxSectionEnum.All
    ).toLowerCase()
    history.push(`/kommunikasjon/epost/${section}`)
  } catch (e) {
    console.log(e)
  }
}

function* emailsSaga() {
  yield takeEvery(EMAILS_FETCH_FOR_WIDGET, fetchWidgetEmails)
  yield takeEvery(EMAILS_FETCH_REQUESTED, fetchEmails)
  yield takeEvery(EMAILS_SET_FILTERS, updateFilters)
  yield takeEvery(EMAILS_FETCH_MORE_REQUESTED, fetchMoreEmails)
  yield takeEvery(EMAIL_SET_CURRENT, fetchEmail)
  yield takeEvery(EMAIL_FETCH_SILENT_REQUESTED, fetchEmailSilent)
  yield takeEvery(
    EMAILS_MARKED_UPDATE_READ_STATUS_SINGLE_REQUEST,
    setReadStatusToUnread
  )
  yield takeEvery(
    EMAILS_MARKED_UPDATE_READ_STATUS_REQUESTED,
    updateEmailReadStatus
  )
  yield takeEvery(EMAILS_FETCH_NEW_SILENTLY_REQUESTED, fetchNewEmailsSilently)
  yield takeEvery(EMAILS_SET_HANDLED_BY_REQUESTED, addHandledByToEmail)
  yield takeEvery(EMAILS_REMOVE_HANDLED_BY_REQUESTED, removeHandledByToEmail)
  yield takeEvery(EMAIL_DELETE_REQUESTED, deleteEmails)
  yield takeEvery(EMAIL_RESTORE_REQUESTED, restoreEmails)
  yield takeEvery(EMAILS_FETCH_STATISTICS_REQUESTED, fetchStatistics)
  yield takeEvery(EMAIL_FETCH_SEEN_BY_REQUESTED, fetchEmailSeenBy)
  yield takeEvery(
    EMAIL_FETCH_SEEN_BY_SUMMARY_REQUESTED,
    fetchEmailSeenBySummary
  )
  yield takeEvery(EMAILS_HANDLE_JUNK_REQUESTED, handleMoveJunkEmails)
  yield takeEvery(EMAIL_RESET_CURRENT, navigateFromCurrent)
}

export default emailsSaga
